import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, Video, Home, User, Phone, Check, X, Clock } from "lucide-react";
import { APPOINTMENT_STATUS } from "@/lib/statusLabels";

const STATUS_LABELS = APPOINTMENT_STATUS;

export default function AdminAppointmentsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  const load = async () => {
    const q = filter ? `?status=${filter}` : "";
    try {
      const { data } = await api.get(`/admin/appointments${q}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
  };

  useEffect(() => {
    load();
    // Mark all as seen on entering this page (clears the badge)
    api.post("/admin/appointments/mark-seen").catch(() => {});
    // Notify the sidebar to refresh
    window.dispatchEvent(new CustomEvent("admin-appointments-seen"));
  }, [filter]); // eslint-disable-line

  const setStatus = async (a, status) => {
    try {
      await api.patch(`/admin/appointments/${a.id}`, { status });
      toast.success("Status atualizado.");
      await load();
    } catch (_) { toast.error("Erro ao atualizar."); }
  };

  const cancel = async (a) => {
    if (!window.confirm(`Cancelar agendamento de ${a.patient_name}?`)) return;
    try {
      await api.delete(`/admin/appointments/${a.id}`);
      toast.success("Agendamento cancelado.");
      await load();
    } catch (_) { toast.error("Erro."); }
  };

  return (
    <div className="space-y-6" data-testid="admin-appointments-page">
      <div>
        <div className="overline text-gold mb-3">Agendamentos</div>
        <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Consultas dos pacientes</h1>
        <p className="text-muted-foreground mt-2">Todos os agendamentos com filtros por status. Confirme, complete ou cancele.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { v: "", l: "Todos" }, { v: "scheduled", l: "Agendados" },
          { v: "confirmed", l: "Confirmados" }, { v: "completed", l: "Concluídos" },
          { v: "cancelled", l: "Cancelados" },
        ].map((o) => (
          <button
            key={o.v}
            onClick={() => setFilter(o.v)}
            className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${filter === o.v ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:bg-muted"}`}
            data-testid={`appt-filter-${o.v || "all"}`}
          >
            {o.l}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-4 py-3 overline text-muted-foreground">Paciente</th>
              <th className="px-4 py-3 overline text-muted-foreground">Serviço</th>
              <th className="px-4 py-3 overline text-muted-foreground">Data</th>
              <th className="px-4 py-3 overline text-muted-foreground">Profissional</th>
              <th className="px-4 py-3 overline text-muted-foreground">Modalidade</th>
              <th className="px-4 py-3 overline text-muted-foreground">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((a) => {
              const s = STATUS_LABELS[a.status] || STATUS_LABELS.scheduled;
              return (
                <tr key={a.id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors" data-testid={`admin-appt-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium inline-flex items-center gap-2">
                      <User className="w-3 h-3 text-muted-foreground" /> {a.patient_name}
                    </div>
                    {a.patient_phone && <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {a.patient_phone}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div>{a.service_name || a.appointment_type}</div>
                    {a.service_price && <div className="text-xs text-gold font-semibold">R$ {Number(a.service_price).toFixed(0)}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1 text-xs"><CalendarDays className="w-3 h-3 text-muted-foreground" /> {new Date(a.scheduled_at).toLocaleDateString("pt-BR")}</div>
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(a.scheduled_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{a.professional || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-xs">
                      {a.appointment_type === "online" ? <Video className="w-3 h-3" /> : <Home className="w-3 h-3" />}
                      {a.appointment_type}
                    </span>
                    {a.appointment_type === "domiciliar" && a.address && (
                      <div className="text-[11px] text-muted-foreground mt-1 max-w-[200px] leading-tight" data-testid={`appt-address-${a.id}`}>
                        {a.address.street}, {a.address.number}{a.address.complement ? ` — ${a.address.complement}` : ""}<br />
                        {a.address.neighborhood} · {a.address.city} · {a.address.zip}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block overline px-2 py-0.5 rounded-sm border ${s.cls}`}>{s.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {a.status === "scheduled" && (
                      <>
                        <button onClick={() => setStatus(a, "confirmed")} className="text-xs text-success font-semibold hover:underline mr-3" data-testid={`confirm-appt-${a.id}`}>Confirmar</button>
                        <button onClick={() => cancel(a)} className="text-xs text-destructive font-semibold hover:underline" data-testid={`cancel-appt-${a.id}`}>Cancelar</button>
                      </>
                    )}
                    {a.status === "confirmed" && (
                      <>
                        <button onClick={() => setStatus(a, "completed")} className="text-xs text-primary font-semibold hover:underline mr-3" data-testid={`complete-appt-${a.id}`}>Concluir</button>
                        <button onClick={() => cancel(a)} className="text-xs text-destructive font-semibold hover:underline" data-testid={`cancel-appt-${a.id}`}>Cancelar</button>
                      </>
                    )}
                    {a.status === "completed" && <Check className="w-4 h-4 text-success inline" />}
                    {a.status === "cancelled" && <X className="w-4 h-4 text-destructive inline" />}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">Nenhum agendamento neste filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
